import Bugsnag from '@bugsnag/js';

export const PUBLIC_SESSION_RETRY_DELAYS_MS: readonly number[] = [1000, 2000, 5000];

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

type ReadFailure = { status?: number } | null | undefined;

/**
 * Réseau coupé, timeout ou serveur indisponible : la session n'a rien dit de son état. Seules les
 * réponses métier (404 introuvable, 401 annulée ou jamais lancée...) disent qu'elle est fermée.
 */
export function isTransientPublicReadFailure(failure: ReadFailure): boolean {
  const status = failure?.status;
  return status === undefined || status === 408 || status === 429 || status >= 500;
}

type PublicSessionArg = { webinarId: number; sessionIdentifier: string };

export function publicSessionReadKey({ webinarId, sessionIdentifier }: PublicSessionArg): string {
  return `${webinarId}:${sessionIdentifier}`;
}

/**
 * Les pages live et salle d'attente se partagent la session courante : un résultat tardif d'une
 * autre session (route quittée) ne doit pas remplacer celle demandée en dernier.
 */
export function isForActivePublicSession(
  activeKey: string | null | undefined,
  arg: PublicSessionArg,
): boolean {
  return activeKey == null || activeKey === publicSessionReadKey(arg);
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/** ±25 % : après une panne collective, toute l'audience ne revient pas à la même milliseconde. */
export function jitterRetryDelay(delayMs: number, random: () => number = Math.random): number {
  return Math.round(delayMs * (0.75 + random() * 0.5));
}

export async function readWithTransientRetry<T>(
  read: () => Promise<T>,
  delaysMs: readonly number[] = PUBLIC_SESSION_RETRY_DELAYS_MS,
  pause: (delayMs: number) => Promise<void> = wait,
  random: () => number = Math.random,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await read();
    } catch (error) {
      const delayMs = delaysMs[attempt];
      if (delayMs === undefined || !isTransientPublicReadFailure(error as ReadFailure)) throw error;
      await pause(jitterRetryDelay(delayMs, random));
    }
  }
}

type SharedRead = { promise: Promise<unknown>; rerun: boolean };

const sharedReads = new Map<string, SharedRead>();

/**
 * Une seule lecture en vol par session, partagée par la salle d'attente et la page du live : une
 * rafale de SESSION_UPDATE ne multiplie pas les chaînes d'essais. Un appel arrivé pendant la
 * lecture obtient une relecture lancée après lui, jamais une valeur antérieure à son arrivée.
 */
export function readPublicSessionShared<T>(
  webinarId: number,
  sessionIdentifier: string,
  read: () => Promise<T>,
  delaysMs: readonly number[] = PUBLIC_SESSION_RETRY_DELAYS_MS,
  pause: (delayMs: number) => Promise<void> = wait,
): Promise<T> {
  const key = publicSessionReadKey({ webinarId, sessionIdentifier });
  const inFlight = sharedReads.get(key);
  if (inFlight) {
    inFlight.rerun = true;
    return inFlight.promise as Promise<T>;
  }

  const entry: SharedRead = { promise: Promise.resolve(), rerun: false };
  entry.promise = (async () => {
    try {
      let session = await readWithTransientRetry(read, delaysMs, pause);
      while (entry.rerun) {
        entry.rerun = false;
        session = await readWithTransientRetry(read, delaysMs, pause);
      }
      return session;
    } finally {
      sharedReads.delete(key);
    }
  })();
  sharedReads.set(key, entry);
  return entry.promise as Promise<T>;
}

export function reportPublicSessionUnavailable(metadata: {
  page: 'waiting-room' | 'signin';
  webinarId?: string;
  sessionIdentifier?: string;
  error: string | null;
}): void {
  if (!isBugsnagEnabled) return;
  try {
    Bugsnag.notify(new Error('Public session unavailable'), (event) => {
      event.severity = 'warning';
      event.addMetadata('publicSession', metadata);
    });
  } catch {
    // le rapport ne doit jamais bloquer l'écran de reprise
  }
}
